/**
 * Page publique de paiement RDV via lien magique.
 *
 * URL: /pay-appointment/:appointment_id?token=XXXX
 *
 * Envoyée par email au client quand l'admin crée un RDV en mode
 * "pending_payment". Le token embarqué dans l'URL fait office d'auth limitée
 * à cette seule action — le client n'a pas besoin de créer un compte ni de
 * se connecter, ce qui évite le taux d'abandon élevé constaté sur l'ancien
 * flow (`/dashboard` obligeait à se logger).
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { SquarePaymentForm } from "../components/SquarePaymentForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayAppointmentByLink() {
  const { appointment_id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Lien invalide — token manquant.");
      setLoading(false);
      return;
    }
    axios.get(`${API}/appointments/pay-link/${appointment_id}`, { params: { token } })
      .then((r) => setDetails(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Impossible de charger le rendez-vous."))
      .finally(() => setLoading(false));
  }, [appointment_id, token]);

  const handlePay = async (sourceId, verificationToken) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/appointments/pay-link/${appointment_id}`, {
        token,
        source_id: sourceId,
        verification_token: verificationToken,
      });
      toast.success("Paiement validé ✅");
      setPaid(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Le paiement n'a pas abouti.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-white flex items-center justify-center p-4" data-testid="pay-appointment-page">
      <div className="w-full max-w-lg bg-[#1B132B] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-white/10 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#F5D8A2]" />
          <h1 className="text-lg font-medium">Paiement de votre rendez-vous</h1>
        </div>

        <div className="p-6">
          {loading && <p className="text-center text-white/60">Chargement…</p>}

          {!loading && error && (
            <div className="text-center py-6" data-testid="pay-error">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
              <p className="text-white/85 mb-2">{error}</p>
              <p className="text-xs text-white/50">
                Si le rendez-vous a déjà été réglé ou annulé, il n&apos;est plus disponible ici.
              </p>
              <Link to="/" className="inline-block mt-5 text-sm text-[#F5D8A2] underline hover:no-underline">
                Retour au site
              </Link>
            </div>
          )}

          {!loading && paid && (
            <div className="text-center py-6" data-testid="pay-success">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-400" />
              <p className="text-white text-lg mb-2">Paiement confirmé ✨</p>
              <p className="text-sm text-white/60 mb-5">
                Un email de confirmation vient d&apos;être envoyé à {details?.customer_email}.
              </p>
              <Link to="/" className="inline-block px-6 py-2.5 bg-[#F5D8A2] text-[#0a0a1a] rounded-full text-xs uppercase tracking-widest font-medium hover:bg-white transition-all">
                Retour au site
              </Link>
            </div>
          )}

          {!loading && !error && !paid && details && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6 space-y-2 text-sm" data-testid="pay-details">
                <div><span className="text-white/50">Prestation :</span> <strong>{details.service_name}</strong>{details.variation_name && ` — ${details.variation_name}`}</div>
                <div><span className="text-white/50">Date :</span> {details.date} à {details.time_slot}</div>
                {details.duration_minutes && <div><span className="text-white/50">Durée :</span> {details.duration_minutes} min</div>}
                <div><span className="text-white/50">Modalité :</span> {details.location_mode === "distance" ? "À distance" : "En boutique"}</div>
                <div className="pt-2 border-t border-white/10 flex justify-between items-center">
                  <span className="text-white/50">Montant à régler</span>
                  <strong className="text-xl text-[#F5D8A2]">{Number(details.price || 0).toFixed(2)} €</strong>
                </div>
              </div>

              <SquarePaymentForm
                amount={Number(details.price || 0)}
                onSuccess={handlePay}
                disabled={processing}
                buttonText="Payer"
                buyerEmail={details.customer_email || ""}
                buyerName={details.customer_name || ""}
              />
              <p className="text-[11px] text-white/40 mt-4 flex items-center gap-1.5 justify-center">
                <Lock className="w-3 h-3" /> Paiement sécurisé Square — CB, Apple Pay, Google Pay
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
